import cornerstoneTools from "cornerstone-tools";

export const FLUID_SEGMENTATION_LABELS = ["IRF", "SRF", "PED", "SHRM"];

/** Always expose all four fluid labels in the UI (IRF, PED, SRF, SHRM). */
export function getActiveFluidLabelsFromApi(_fluidSegmentation) {
  return [...FLUID_SEGMENTATION_LABELS];
}

export function isTopconManufacturer(manufacturer) {
  return String(manufacturer || "").trim().toLowerCase() === "topcon";
}

/** Fluid labels included in compute-volumes payload (non-Topcon omits SHRM). */
export function getFluidLabelsForVolumesApi(manufacturer) {
  if (isTopconManufacturer(manufacturer)) {
    return [...FLUID_SEGMENTATION_LABELS];
  }
  return ["IRF", "SRF", "PED"];
}

/** Fluids present in a compute-volumes API response (regions + totals). */
export function getFluidLabelsFromVolumesData(volumesData) {
  const labels = new Set();
  const regions = volumesData?.regions;
  const totals = volumesData?.totals;

  if (regions && typeof regions === "object") {
    Object.values(regions).forEach((region) => {
      if (!region || typeof region !== "object") return;
      Object.keys(region).forEach((label) => {
        if (FLUID_SEGMENTATION_LABELS.includes(label)) labels.add(label);
      });
    });
  }

  if (totals && typeof totals === "object") {
    Object.keys(totals).forEach((label) => {
      if (FLUID_SEGMENTATION_LABELS.includes(label)) labels.add(label);
    });
  }

  return FLUID_SEGMENTATION_LABELS.filter((label) => labels.has(label));
}

export const FLUID_SEGMENTATION_DEFAULT_COLORS = {
  IRF: "rgba(255, 0, 0, 1)",
  SRF: "rgba(0, 255, 0, 1)",
  PED: "rgba(0, 0, 255, 1)",
  SHRM: "rgba(255, 255, 0, 1)",
};

/** Panel label text colors — always use canonical mapping, not user prefs. */
export const FLUID_LABEL_TEXT_COLORS = {
  IRF: "#e53935",
  SRF: "#2e7d32",
  PED: "#1565c0",
  SHRM: "#f9a825",
};

export function fluidLabelTextColor(label) {
  return FLUID_LABEL_TEXT_COLORS[String(label).toUpperCase()] || "#333";
}

/** @typedef {{ x: number, y: number }} ImgPt */

function clamp(v, lo, hi) {
  return Math.min(hi, Math.max(lo, v));
}

/** Fluid manual Brush/Paint slider (image pixels); matches VTK/Vue picker range */
export const FLUID_MANUAL_BRUSH_RADIUS_MIN = 1;
export const FLUID_MANUAL_BRUSH_RADIUS_MAX = 50;

/** Canvas stroke width for each fluid row (image pixels); will move to 1px later. */
export const FLUID_ROW_LINE_WIDTH_IMAGE_PX = 0.5;

/** Clamp brush disc radius used for stamping and preview (image pixels). */
export function clampFluidManualBrushRadius(raw) {
  if (typeof raw !== "number" || !Number.isFinite(raw)) return 6;
  return Math.round(
    clamp(
      raw,
      FLUID_MANUAL_BRUSH_RADIUS_MIN,
      FLUID_MANUAL_BRUSH_RADIUS_MAX
    )
  );
}

/** Merge overlapping [lo,hi] intervals on same row (in pixel space). */
export function mergeIntervals(intervals) {
  if (!intervals.length) return [];
  const s = [...intervals].sort((a, b) => a.lo - b.lo);
  const out = [];
  let cur = { ...s[0] };
  for (let i = 1; i < s.length; i++) {
    const n = s[i];
    if (n.lo <= cur.hi + 1) {
      cur.hi = Math.max(cur.hi, n.hi);
    } else {
      out.push(cur);
      cur = { ...n };
    }
  }
  out.push(cur);
  return out;
}

/** Stamp every integer pixel covered by a disc (pixel-center distance test). */
function stampDiscPixels(pixels, px, py, rDisc, columns, rows) {
  const r2 = rDisc * rDisc;
  const y0 = Math.max(0, Math.floor(py - rDisc));
  const y1 = Math.min(rows - 1, Math.ceil(py + rDisc));
  const x0 = Math.max(0, Math.floor(px - rDisc));
  const x1 = Math.min(columns - 1, Math.ceil(px + rDisc));
  for (let y = y0; y <= y1; y++) {
    const cy = y + 0.5;
    for (let x = x0; x <= x1; x++) {
      const cx = x + 0.5;
      if ((cx - px) ** 2 + (cy - py) ** 2 <= r2 + 1e-9) {
        pixels.add(`${x},${y}`);
      }
    }
  }
}

/** Group stamped pixels into horizontal row segments (integer x columns, integer y rows). */
function pixelSetToRowDatums(pixels, label, color) {
  /** @type {Map<number, number[]>} */
  const xsByY = new Map();
  pixels.forEach((key) => {
    const [xs, ys] = key.split(",");
    const x = Number(xs);
    const y = Number(ys);
    if (!Number.isFinite(x) || !Number.isFinite(y)) return;
    if (!xsByY.has(y)) xsByY.set(y, []);
    xsByY.get(y).push(x);
  });

  /** @type {Array<{start:ImgPt,end:ImgPt,label:string,color:string}>} */
  const out = [];
  const sortedYs = [...xsByY.keys()].sort((a, b) => a - b);
  sortedYs.forEach((y) => {
    const xs = xsByY.get(y).sort((a, b) => a - b);
    let runStart = xs[0];
    let runEnd = xs[0];
    for (let i = 1; i < xs.length; i++) {
      if (xs[i] === runEnd + 1) {
        runEnd = xs[i];
      } else {
        out.push({
          start: { x: runStart, y },
          end: { x: runEnd, y },
          label,
          color,
        });
        runStart = xs[i];
        runEnd = xs[i];
      }
    }
    out.push({
      start: { x: runStart, y },
      end: { x: runEnd, y },
      label,
      color,
    });
  });
  return out;
}

/** Point-in-polygon test for pixel center (x+0.5, y+0.5). */
function isPixelCenterInsidePolygon(x, y, poly) {
  const px = x + 0.5;
  const py = y + 0.5;
  let inside = false;
  const n = poly.length;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const xi = poly[i][0];
    const yi = poly[i][1];
    const xj = poly[j][0];
    const yj = poly[j][1];
    const intersect =
      yi > py !== yj > py &&
      px < ((xj - xi) * (py - yi)) / (yj - yi + 0) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

export function interpolateSegment(p0, p1, step) {
  /** @type {ImgPt[]} */
  const pts = [];
  const dx = p1.x - p0.x;
  const dy = p1.y - p0.y;
  const d = Math.sqrt(dx * dx + dy * dy);
  if (d < 1e-9) return [p0];
  const n = Math.max(1, Math.ceil(d / step));
  for (let i = 0; i <= n; i++) {
    const t = i / n;
    pts.push({ x: p0.x + t * dx, y: p0.y + t * dy });
  }
  return pts;
}

/**
 * Expand centerline polyline into horizontal row segments by stamping a disc
 * at each sample (image pixels). Uses integer pixel rasterization so every
 * covered row/column is present (no missing y lines).
 * @returns {Array<{start:ImgPt,end:ImgPt,label:string,color:string}>}
 */
export function centerlineToFluidRowDatums(points, r, label, color, columns, rows) {
  const rDisc = clampFluidManualBrushRadius(r);
  if (!points.length || rDisc <= 0) return [];

  /** @type {Set<string>} */
  const pixels = new Set();

  const step = Math.max(0.25, rDisc * 0.25);
  if (points.length === 1) {
    stampDiscPixels(pixels, points[0].x, points[0].y, rDisc, columns, rows);
  } else {
    for (let k = 0; k < points.length - 1; k++) {
      const pts = interpolateSegment(points[k], points[k + 1], step);
      pts.forEach((p) =>
        stampDiscPixels(pixels, p.x, p.y, rDisc, columns, rows)
      );
    }
  }

  return pixelSetToRowDatums(pixels, label, color);
}

/**
 * Fill polygon interior as horizontal row segments (integer pixels).
 * @param {ImgPt[]} polyPoints — closed or open (will close if needed)
 */
export function polygonFillToFluidRowDatums(polyPoints, label, color, columns, rows) {
  if (!polyPoints || polyPoints.length < 3) return [];
  const poly = polyPoints.map((p) => [p.x, p.y]);
  const first = poly[0];
  const last = poly[poly.length - 1];
  if (first[0] !== last[0] || first[1] !== last[1]) {
    poly.push([first[0], first[1]]);
  }

  let xmin = Infinity;
  let xmax = -Infinity;
  let ymin = Infinity;
  let ymax = -Infinity;
  poly.forEach(([px, py]) => {
    xmin = Math.min(xmin, px);
    xmax = Math.max(xmax, px);
    ymin = Math.min(ymin, py);
    ymax = Math.max(ymax, py);
  });

  const xStart = clamp(Math.floor(xmin), 0, columns - 1);
  const xEnd = clamp(Math.ceil(xmax), 0, columns - 1);
  const yStart = clamp(Math.floor(ymin), 0, rows - 1);
  const yEnd = clamp(Math.ceil(ymax), 0, rows - 1);

  /** @type {Set<string>} */
  const pixels = new Set();
  for (let y = yStart; y <= yEnd; y++) {
    for (let x = xStart; x <= xEnd; x++) {
      if (isPixelCenterInsidePolygon(x, y, poly)) {
        pixels.add(`${x},${y}`);
      }
    }
  }

  return pixelSetToRowDatums(pixels, label, color);
}

/** Snap a horizontal fluid datum to integer image coordinates. */
export function normalizeFluidDatum(datum) {
  if (!datum?.start || !datum?.end) return datum;
  const y = Math.round(datum.start.y);
  const x1 = Math.round(Math.min(datum.start.x, datum.end.x));
  const x2 = Math.round(Math.max(datum.start.x, datum.end.x));
  return {
    ...datum,
    start: { x: x1, y },
    end: { x: x2, y },
  };
}

/**
 * Merge touching/overlapping row segments per label+y (API-style rows).
 * @returns {Array<{start:ImgPt,end:ImgPt,label:string,color?:string,alpha?:number}>}
 */
export function mergeAdjacentFluidRowDatums(datums) {
  if (!Array.isArray(datums) || !datums.length) return [];

  /** @type {Map<string, {lo:number,hi:number}[]>} */
  const byKey = new Map();
  /** @type {Map<string, {label:string,color?:string,alpha?:number,visible?:boolean}>} */
  const metaByKey = new Map();

  datums.forEach((d) => {
    if (!d?.start || !d?.end || !d.label) return;
    const norm = normalizeFluidDatum(d);
    const y = norm.start.y;
    const key = `${String(d.label).toUpperCase()}|${y}`;
    if (!byKey.has(key)) {
      byKey.set(key, []);
      metaByKey.set(key, {
        label: String(d.label).toUpperCase(),
        color: d.color,
        alpha: d.alpha,
        visible: d.visible !== false,
      });
    }
    byKey.get(key).push({
      lo: Math.min(norm.start.x, norm.end.x),
      hi: Math.max(norm.start.x, norm.end.x),
    });
  });

  /** @type {typeof datums} */
  const out = [];
  byKey.forEach((ivals, key) => {
    const meta = metaByKey.get(key);
    const y = Number(key.split("|")[1]);
    const merged = mergeIntervals(
      ivals.map((iv) => ({ lo: iv.lo, hi: iv.hi }))
    );
    merged.forEach((iv) => {
      const x1 = Math.round(iv.lo);
      const x2 = Math.round(iv.hi);
      if (x2 < x1) return;
      out.push({
        visible: meta.visible !== false,
        active: true,
        invalidated: true,
        alpha: typeof meta.alpha === "number" ? meta.alpha : 0.35,
        label: meta.label,
        color: meta.color,
        start: { x: x1, y },
        end: { x: x2, y },
      });
    });
  });
  return out;
}

/**
 * Merge API segments into horizontal row lines `[x1, x2, y]`.
 * Accepts both merged spans and legacy per-column `[x, x, y]` entries.
 */
export function coalesceApiSegmentsToRowLines(segments) {
  if (!Array.isArray(segments) || !segments.length) return [];

  /** @type {Map<number, {lo:number,hi:number}[]>} */
  const byY = new Map();

  segments.forEach((seg) => {
    if (!Array.isArray(seg) || seg.length !== 3) return;
    const x1 = Number(seg[0]);
    const x2 = Number(seg[1]);
    const y = Math.round(Number(seg[2]));
    if (!Number.isFinite(x1) || !Number.isFinite(x2) || !Number.isFinite(y)) {
      return;
    }
    const lo = Math.min(Math.round(x1), Math.round(x2));
    const hi = Math.max(Math.round(x1), Math.round(x2));
    if (!byY.has(y)) byY.set(y, []);
    byY.get(y).push({ lo, hi });
  });

  /** @type {number[][]} */
  const out = [];
  [...byY.keys()]
    .sort((a, b) => a - b)
    .forEach((y) => {
      mergeIntervals(byY.get(y)).forEach((iv) => {
        out.push([iv.lo, iv.hi, y]);
      });
    });
  return out;
}

/** Convert tool-state datums to API `[x1, x2, y]` row lines for one label. */
export function fluidDatumsToApiSegments(datums, label) {
  const L = String(label).toUpperCase();
  const raw = (datums || [])
    .filter(
      (d) =>
        d?.start &&
        d?.end &&
        String(d.label).toUpperCase() === L &&
        d.visible !== false &&
        !d._manualFluidDraft
    )
    .map((d) => {
      const norm = normalizeFluidDatum(d);
      return [norm.start.x, norm.end.x, norm.start.y];
    });

  return coalesceApiSegmentsToRowLines(raw);
}

/** Extract all fluid labels on a frame as API-format segments. */
export function extractFluidApiSegmentsByLabel(datums) {
  const out = {};
  FLUID_SEGMENTATION_LABELS.forEach((label) => {
    const segs = fluidDatumsToApiSegments(datums, label);
    if (segs.length) out[label] = segs;
  });
  return out;
}

export function fluidLabelColorFromSettings(_settings, label) {
  const L = String(label).toUpperCase();
  const fallback = FLUID_SEGMENTATION_DEFAULT_COLORS[L];
  if (fallback) return fallback.replace(/,\s*1\s*\)$/, ", 0.75)");
  return "rgba(255, 255, 0, 0.55)";
}

/**
 * Replace any draft manual-fluid entries then append finalized datums.
 */
export function commitFluidManualDatums(imageId, datums, metaCommon) {
  if (!imageId || !datums?.length) return false;
  const mgr = cornerstoneTools.globalImageIdSpecificToolStateManager;
  if (!mgr?.saveImageIdToolState || !mgr?.restoreImageIdToolState) return false;

  const bundle = mgr.saveImageIdToolState(imageId) || {};
  let fd = bundle.FluidSegmentation;
  if (!fd) fd = { data: [] };
  if (!Array.isArray(fd.data)) fd.data = [];

  fd.data = fd.data.filter((d) => !d._manualFluidDraft);

  const merged = mergeAdjacentFluidRowDatums(
    fd.data
      .filter((d) => !d._manualFluidDraft)
      .concat(datums.map((d) => normalizeFluidDatum(d)))
  );

  fd.data = merged;

  bundle.FluidSegmentation = fd;
  mgr.restoreImageIdToolState(imageId, bundle);
  return true;
}

/** Live preview while dragging — same horizontal row segments as committed/API fluid. */
export function upsertFluidManualDraftRowSegments(
  imageId,
  points,
  label,
  color,
  alpha = 0.45,
  draftStrokeRadiusImage = 10,
  columns,
  rows
) {
  if (!imageId || !points?.length) return;
  const mgr = cornerstoneTools.globalImageIdSpecificToolStateManager;
  if (!mgr?.saveImageIdToolState || !mgr?.restoreImageIdToolState) return;

  const bundle = mgr.saveImageIdToolState(imageId) || {};
  let fd = bundle.FluidSegmentation;
  if (!fd) fd = { data: [] };
  if (!Array.isArray(fd.data)) fd.data = [];

  const rad = clampFluidManualBrushRadius(draftStrokeRadiusImage);
  const rowDatums = centerlineToFluidRowDatums(
    points,
    rad,
    label,
    color,
    columns,
    rows
  );

  const base = fd.data.filter((d) => !d._manualFluidDraft);
  const draftRows = rowDatums.map((d) => {
    const norm = normalizeFluidDatum(d);
    return {
      _manualFluidDraft: true,
      visible: true,
      active: true,
      invalidated: true,
      alpha,
      label,
      color,
      start: norm.start,
      end: norm.end,
    };
  });

  fd.data = mergeAdjacentFluidRowDatums(
    base.concat(draftRows)
  );

  bundle.FluidSegmentation = fd;
  mgr.restoreImageIdToolState(imageId, bundle);
}

/** @deprecated Use upsertFluidManualDraftRowSegments — kept for imports. */
export function upsertFluidManualDraftPolyline(
  imageId,
  points,
  label,
  color,
  alpha = 0.45,
  draftStrokeRadiusImage = 10,
  columns = 512,
  rows = 512
) {
  upsertFluidManualDraftRowSegments(
    imageId,
    points,
    label,
    color,
    alpha,
    draftStrokeRadiusImage,
    columns,
    rows
  );
}

/** Replace fluid data for one image with merged row segments (after erase, etc.). */
export function replaceFluidSegmentationData(imageId, dataArr) {
  if (!imageId) return false;
  const mgr = cornerstoneTools.globalImageIdSpecificToolStateManager;
  if (!mgr?.saveImageIdToolState || !mgr?.restoreImageIdToolState) return false;

  const bundle = mgr.saveImageIdToolState(imageId) || {};
  bundle.FluidSegmentation = {
    data: mergeAdjacentFluidRowDatums(
      (dataArr || []).filter((d) => !d._manualFluidDraft)
    ),
  };
  mgr.restoreImageIdToolState(imageId, bundle);
  return true;
}

export function removeFluidManualDraft(imageId) {
  if (!imageId) return;
  const mgr = cornerstoneTools.globalImageIdSpecificToolStateManager;
  if (!mgr?.saveImageIdToolState || !mgr?.restoreImageIdToolState) return;
  const bundle = mgr.saveImageIdToolState(imageId);
  if (!bundle?.FluidSegmentation?.data) return;
  bundle.FluidSegmentation.data = bundle.FluidSegmentation.data.filter(
    (d) => !d._manualFluidDraft
  );
  mgr.restoreImageIdToolState(imageId, bundle);
}
